import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://dallasdesiscoop.com";
const DEFAULT_IMAGE = "https://dallasdesiscoop.com/apple-touch-icon.png";
// The actual logo mark rendered in the site Header/Footer — used for the
// Organization JSON-LD "logo" field, distinct from DEFAULT_IMAGE (a hero/OG
// social-share photo, not a logo).
const ORG_LOGO = "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784770864829-dt7lq8.png";
const SITE_NAME = "Dallas Desi Scoop";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

// Sitewide Organization / WebSite structured data — included on every
// page so search engines always have a consistent entity graph to anchor to,
// even on pages that don't pass their own jsonLd prop.
const ORG_JSONLD: Record<string, unknown>[] = [
  {
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    url: BASE_URL,
    name: SITE_NAME,
    description:
      "Dallas Desi Scoop is the number one guide for the South Asian community in Dallas-Fort Worth. Indian restaurants, events, grocery stores, temples, and businesses.",
    publisher: { "@id": `${BASE_URL}/#organization` },
    // NOTE: no SearchAction/potentialAction here. The site's search is a
    // client-side modal, not a URL-based results page, so there is no real
    // target for a ?q= search URL. Advertising one made Google crawl the
    // literal placeholder /?q={search_term_string} and flag it as a soft 404.
  },
  {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    name: SITE_NAME,
    url: `${BASE_URL}/`,
    image: DEFAULT_IMAGE,
    logo: ORG_LOGO,
    description:
      "Dallas Desi Scoop connects the South Asian community across Dallas-Fort Worth with Indian restaurants, events, temples, grocery stores, and local businesses.",
    areaServed: {
      "@type": "City",
      name: "Dallas-Fort Worth",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Dallas",
      addressRegion: "TX",
      addressCountry: "US",
    },
  },
];

// SEO renders everything declaratively through react-helmet-async's <Helmet>
// instead of imperatively mutating document.head inside a useEffect. This
// matters for prerendering: useEffect never runs during a Node-side
// ReactDOMServer.renderToString() pass, so the old imperative version
// produced prerendered HTML with NO title, NO meta description, and NO
// JSON-LD at all — exactly the content Google needs to see without running
// JS. <Helmet> tags are collected by the HelmetProvider's server context
// during renderToString and can be pulled out synchronously right after, so
// the prerender script can inject them into the static HTML shell.
export default function SEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = DEFAULT_IMAGE,
  noindex = false,
  jsonLd,
}: SEOProps) {
  const location = useLocation();
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

  // Auto-detect admin routes and force noindex
  const isAdminRoute = location.pathname.startsWith("/admin");
  const shouldNoindex = noindex || isAdminRoute;

  const pageGraph = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const fullGraph = [...ORG_JSONLD, ...pageGraph];
  const jsonLdPayload = { "@context": "https://schema.org", "@graph": fullGraph };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={shouldNoindex ? "noindex,nofollow" : "index, follow"} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <script id="page-jsonld" type="application/ld+json">
        {JSON.stringify(jsonLdPayload)}
      </script>
    </Helmet>
  );
}
